import { GlobalLogger } from "./debugLogger";
import CLASSES from "../assets/model-tfjs/class_names.json";
import { DetectionResults, Prediction } from "./types/Detector";
import { Tensor } from "@tensorflow/tfjs";

/* eslint-disable @typescript-eslint/no-var-requires */
const tf = require("@tensorflow/tfjs");
const { loadGraphModel } = require("@tensorflow/tfjs-converter");

interface EmbedModel {
  model: any; // TensorFlow model
  layerName: string | null;
}

declare global {
  interface Window {
    cardDetector?: CardDetector;
  }
}

class CardDetector {
  //model is fixed to IMG_SIZE x IMG_SIZE
  public static IMG_SIZE = 224;
  private model: any;
  private customModelPath = "";
  private model_name = "model-v5-tfjs";
  private cdnPath = `https://cardscan-models.s3-accelerate.amazonaws.com/card-detection-ft-class/${this.model_name}/model.json`;
  public isFlippedSession = false;
  private statusCallback: ((state: string) => void) | null = null;
  private _modelState: string;
  private laplaceFilter = tf
    .tensor2d([
      [0, 1, 0],
      [1, -4, 1],
      [0, 1, 0],
    ])
    .expandDims(-1)
    .expandDims(-1);

  /** 5x5 Gaussian-like blur kernel for RGB images used in blur detection */
  private blurKernel = tf.tidy(() => {
    const kernel2d = tf
      .tensor2d([
        [1, 4, 6, 4, 1],
        [4, 16, 24, 16, 4],
        [6, 24, 36, 24, 6],
        [4, 16, 24, 16, 4],
        [1, 4, 6, 4, 1],
      ])
      .div(256);

    // Create 3-channel version: [5, 5, 3, 1]
    return tf.stack([kernel2d, kernel2d, kernel2d], 2).expandDims(-1);
  });

  // Embedding functionality for card flip detection
  private embedModel: EmbedModel | null = null;
  private referenceEmbedding: Float32Array | null = null;

  public constructor(modelPath?: string) {
    GlobalLogger.earlyLog("Detector -- init");
    this._modelState = "loading";
    if (modelPath) this.customModelPath = modelPath;
    this.load_model().then((model) => {
      this.model = model;
      this.setModelState("loaded");
      this.warm_model();
    });
    //TODO: catch error loading model....
  }

  public setModelStatusCallback(callback: (state: string) => void) {
    this.statusCallback = callback;
  }

  private setModelState(state: string) {
    this._modelState = state;
    if (this.statusCallback) {
      this.statusCallback(state);
    }
  }

  get modelState(): string {
    return this._modelState;
  }

  private async load_model() {
    let model;
    const t0 = performance.now();
    try {
      model = await loadGraphModel(`indexeddb://cardscan-${this.model_name}`);
      GlobalLogger.earlyLog("Detector -- model loaded from cache");
    } catch {
      try {
        if (this.customModelPath) {
          model = await loadGraphModel(this.customModelPath);
          GlobalLogger.earlyLog("Detector -- model loaded from local file");
        } else {
          throw new Error("No custom model path provided");
        }
      } catch (localFileError) {
        GlobalLogger.earlyLog(
          `Detector -- local file loaded failed: ${localFileError}`
        );
        try {
          model = await loadGraphModel(this.cdnPath);
          GlobalLogger.earlyLog("Detector -- model loaded from CDN");
        } catch (cdnError) {
          GlobalLogger.earlyLog(`Detector -- CDN load failed: ${cdnError}`);
          throw new Error(
            "Failed to load model from cache, local file, and CDN."
          );
        }
      }
    }
    const t1 = performance.now();
    GlobalLogger.earlyLog(`Detector -- model loaded in ${t1 - t0} ms`);
    return model;
  }

  private detectDistanceProblems(
    predictions: Prediction[],
    laplaceVariance: number,
    blurScore: number
  ) {
    const top = predictions[0];
    const highConfidence = top.probability > 0.95;
    const lowDetail = laplaceVariance < 0.04;
    const likelyBlurry = blurScore > 0.92;

    const likelyTooFar = highConfidence && lowDetail;

    return {
      likelyTooFar,
      shouldReject: likelyTooFar,
      confidence: top.probability,
      laplaceVariance,
      blurScore,
      likelyBlurry,
    };
  }

  private warm_model() {
    if (this.model == null) {
      GlobalLogger.error("Detector -- Unable to warm unloaded model");
      return;
    }

    //TOOD: check https, check IP isn't HOME.
    const expression =
      /((^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$))/;

    //trying to stop other devs from wasting time.
    if (
      expression.test(window.location.hostname) &&
      window.location.hostname != "127.0.0.1"
    ) {
      GlobalLogger.earlyLog(
        "many browsers do not allow camera access on IP addresses. Please use localhost (web) or a tunnel like ngrox (mobile)"
      );

      //TODO: if dev/sandbox and on mobile and on ip... then show alert...
    }

    if (
      window.location.protocol != "https:" &&
      window.location.protocol != "file:" &&
      window.location.hostname != "localhost" &&
      window.location.hostname != "127.0.0.1"
    ) {
      GlobalLogger.earlyLog(
        "CardScanView: many browsers do not allow camera access on insecure connections. Please use localhost or https"
      );
    }

    // Setup embedding extraction after model warm-up

    tf.tidy(() => {
      const t0 = performance.now();
      const results = this.model.predict(
        tf
          .zeros([CardDetector.IMG_SIZE, CardDetector.IMG_SIZE, 3], "float32")
          .expandDims(0)
      );

      results.data().then(() => {
        this.setModelState("warmed");
        //save model on successful warm
        this.model.save(`indexeddb://cardscan-${this.model_name}`);
        // Setup embedding model after successful warm
        this.setupEmbeddingModel();
      });
      const t1 = performance.now();
      GlobalLogger.earlyLog(`Detector -- model warmed in ${t1 - t0} ms`);
      GlobalLogger.earlyLog(this.tf_mem_log(tf.memory()));
    });
  }
  private tf_mem_log(memory: any) {
    return `TF Memory Usage - t:${memory.numTensors}, gpu:${memory.numBytesInGPU}, gpufree:${memory.numBytesInGPUFree} buf:${memory.numDataBuffers}, mem:${memory.numBytes}`;
  }

  private setupEmbeddingModel() {
    try {
      // Use the final conv layer for embeddings
      const embeddingLayerName =
        "StatefulPartitionedCall/sequential/keras_layer/StatefulPartitionedCall/StatefulPartitionedCall/StatefulPartitionedCall/predict/MobilenetV1/MobilenetV1/Conv2d_13_pointwise/Relu6";

      try {
        // Test with a dummy input
        const testInput = tf.zeros([1, 224, 224, 3]);
        const testOutput = this.model.execute(testInput, embeddingLayerName);

        if (testOutput) {
          this.embedModel = {
            model: this.model,
            layerName: embeddingLayerName,
          };
          testOutput.dispose();
          testInput.dispose();
          GlobalLogger.earlyLog("Embedding model setup complete");
        } else {
          throw new Error("Layer execution returned null");
        }
      } catch (e) {
        GlobalLogger.error(`Embedding layer not accessible: ${e}`);
        GlobalLogger.earlyLog("Disabling embedding-based card flip detection");
        this.embedModel = null;
      }
    } catch (error) {
      GlobalLogger.error(`Failed to setup embedding model: ${error}`);
      this.embedModel = { model: this.model, layerName: null };
    }
  }

  private extractEmbedding(imgTensor: any): any | null {
    if (!this.embedModel) {
      GlobalLogger.error("Embedding model not available - returning null");
      return null;
    }

    try {
      return tf.tidy(() => {
        let features;

        // Validate input tensor
        if (!imgTensor || !imgTensor.shape) {
          GlobalLogger.error(
            "Invalid input tensor for embedding extraction - returning null"
          );
          return null;
        }

        const embedModel = this.embedModel!; // Null check already done above

        if (embedModel.layerName) {
          try {
            // Use intermediate layer (256-dimensional embeddings)
            features = embedModel.model.execute(
              imgTensor,
              embedModel.layerName
            );

            if (!features) {
              GlobalLogger.error("Model execution returned null");
              return null;
            }
          } catch (executeError) {
            GlobalLogger.error(
              `Error executing intermediate layer: ${executeError}`
            );
            try {
              features = embedModel.model.predict(imgTensor);
              if (!features) {
                GlobalLogger.error("Fallback predict() also returned null");
                return null;
              }
            } catch (fallbackError) {
              GlobalLogger.error(
                `Fallback predict() also failed: ${fallbackError}`
              );
              return null;
            }
          }
        } else {
          try {
            features = embedModel.model.predict(imgTensor);
            if (!features) {
              GlobalLogger.error("Predict() returned null");
              return null;
            }
          } catch (predictError) {
            GlobalLogger.error(`Predict() failed: ${predictError}`);
            return null;
          }
        }

        // Validate features tensor
        if (!features || !features.shape) {
          GlobalLogger.error("Features tensor is null or has no shape");
          return null;
        }

        // Handle different output shapes based on which layer we're using
        let embedding;

        try {
          if (features.shape.length === 4) {
            // 4D tensor (batch, height, width, channels) - apply global average pooling
            embedding = features.mean([1, 2]).squeeze(); // Results in 256-dim vector
          } else if (features.shape.length === 3) {
            // 3D tensor (height, width, channels) - apply global average pooling
            embedding = features.mean([0, 1]); // Results in 256-dim vector
          } else if (features.shape.length === 2) {
            // 2D tensor (batch, features) - just squeeze to remove batch dimension
            embedding = features.squeeze();
          } else if (features.shape.length === 1) {
            // Already 1D - use as is
            embedding = features;
          } else {
            // Other shapes - flatten
            embedding = features.flatten();
          }

          if (!embedding) {
            GlobalLogger.error("Embedding processing resulted in null");
            return null;
          }

          // Validate embedding before normalization
          if (embedding.shape[0] === 0) {
            GlobalLogger.error("Embedding tensor has zero dimensions");
            return null;
          }

          // L2 normalize the embedding
          const norm = tf.norm(embedding, 2);
          const normalizedEmbedding = embedding.div(norm.add(1e-8));

          // Final validation
          if (!normalizedEmbedding || normalizedEmbedding.shape[0] === 0) {
            GlobalLogger.error("Normalized embedding is invalid");
            return null;
          }

          return normalizedEmbedding;
        } catch (processingError) {
          GlobalLogger.error(
            `Error during embedding processing: ${processingError}`
          );
          return null;
        }
      });
    } catch (error) {
      GlobalLogger.error(`Error in extractEmbedding: ${error}`);
      return null;
    }
  }

  private setReferenceCard(imgTensor: any): void {
    try {
      const newEmbedding = this.extractEmbedding(imgTensor);
      if (newEmbedding) {
        // Convert tensor to JavaScript array - much simpler!
        this.referenceEmbedding = newEmbedding.dataSync() as Float32Array;
        newEmbedding.dispose(); // Clean up the tensor
      } else {
        GlobalLogger.error("Failed to extract reference embedding");
      }
    } catch (error) {
      GlobalLogger.error(`Error setting reference card: ${error}`);
    }
  }

  private calculateCardSimilarity(imgTensor: any): number {
    if (!this.referenceEmbedding) {
      return 1.0; // No reference yet
    }

    try {
      // Convert current embedding to array
      const currData = tf.tidy(() => {
        const emb = this.extractEmbedding(imgTensor)!;
        return emb.dataSync() as Float32Array;
      });

      // Simple dot product calculation (both embeddings are normalized)
      let dotProduct = 0;
      for (let i = 0; i < this.referenceEmbedding.length; i++) {
        dotProduct += this.referenceEmbedding[i] * currData[i];
      }

      const similarity = Math.max(0, Math.min(1, dotProduct)); // Clamp to [0, 1]

      // Log similarity for debugging
      GlobalLogger.debug(
        `Card similarity: ${similarity.toFixed(3)}`,
        "detector"
      );

      return similarity;
    } catch (error) {
      GlobalLogger.error(`Error calculating similarity: ${error}`);
      return 1.0;
    }
  }

  private processCardSimilarity(
    results: DetectionResults,
    imgTensor: any
  ): void {
    if (!this.embedModel) {
      GlobalLogger.debug(
        "Embedding model not available, skipping similarity",
        "detector"
      );
      return;
    }

    try {
      const embeddingT0 = performance.now();

      // Check if we detected a card (front or back) with high confidence
      const cardDetected = results.allPredictions.find(
        (pred: Prediction) =>
          (pred.className === "card_front" || pred.className === "card_back") &&
          pred.probability > 0.8
      );

      let cardSimilarity = 1.0;
      let hasReference = false;

      // For first valid card detection, set as reference
      if (!this.referenceEmbedding && cardDetected) {
        GlobalLogger.debug(
          `Setting reference: ${cardDetected.className}`,
          "detector"
        );
        this.setReferenceCard(imgTensor);
      }
      // Calculate similarity for subsequent detections
      else if (this.referenceEmbedding && cardDetected) {
        cardSimilarity = this.calculateCardSimilarity(imgTensor);
        hasReference = true;
      }

      // Update the results with similarity data
      results.cardSimilarity = cardSimilarity;
      results.isSameCard = cardSimilarity > 0.85;
      results.hasReference = hasReference;

      const embeddingTime = performance.now() - embeddingT0;
      GlobalLogger.debug(
        `Embedding processing time: ${embeddingTime.toFixed(1)} ms`,
        "detector"
      );
    } catch (embeddingError) {
      GlobalLogger.error(`Error in embedding processing: ${embeddingError}`);
      this.cleanupEmbeddings();
    }
  }

  public cleanupEmbeddings() {
    try {
      GlobalLogger.debug("Disabling embedding functionality", "detector");
      this.referenceEmbedding = null; // Just null it out - it's a simple array now
      this.embedModel = null;
    } catch (error) {
      GlobalLogger.error(`Error during embedding cleanup: ${error}`);
    }
  }

  public disposeAll(): void {
    this.cleanupEmbeddings();

    this.laplaceFilter.dispose();
    this.blurKernel.dispose();
  }

  /**
   * Detects image blur by comparing embeddings of original vs artificially blurred image.
   *
   * @param imgTensor - Input image tensor [1, 224, 224, 3]
   * @returns Blur detection results or null if embeddings not available
   *
   * @description
   * - Blur score: 0.0 (very sharp) to 1.0 (very blurry)
   * - Sharp images change significantly when blurred → low score
   * - Blurry images barely change when blurred more → high score
   * - Current threshold: < 0.90 = sharp, ≥ 0.90 = blurry
   */
  private detectBlur(
    imgTensor: any
  ): { isSharp: boolean; blurScore: number } | null {
    if (!this.embedModel) {
      return null;
    }

    const [isSharp, blurScore] = tf.tidy(() => {
      const originalEmb = this.extractEmbedding(imgTensor)!;
      const blurredImg = tf.depthwiseConv2d(
        imgTensor,
        this.blurKernel,
        1,
        "same"
      );
      const blurredEmb = this.extractEmbedding(blurredImg)!;

      const cosDist = tf.losses.cosineDistance(originalEmb, blurredEmb, 0);
      const distVal = cosDist.dataSync()[0];
      // Compare embeddings using cosine similarity
      // Higher similarity = embeddings are similar = image didn't change much when blurred = already blurry
      // Lower similarity = embeddings are different = image changed a lot when blurred = originally sharp
      const similarity = 1 - distVal;
      // Blur score interpretation:
      // - High score (→1.0): Already blurry (adding blur doesn't change much)
      // - Low score (→0.0): Sharp (adding blur changes embedding significantly)
      const isSharp = similarity < 0.92; // Threshold: adjust based on testing

      return [isSharp, similarity] as [boolean, number];
    });

    GlobalLogger.debug(
      `Blur detection - score: ${blurScore.toFixed(3)}, sharp: ${isSharp}`,
      "detector"
    );
    return { isSharp, blurScore };
  }

  private prepare_results(
    modelResult: any,
    laplaceResult: any
  ): DetectionResults {
    const allScores = Array.from(modelResult.dataSync()) as number[];

    const allPredictions: Prediction[] = allScores
      .map((score: number, i: number) => ({
        probability: score,
        className: CLASSES[i],
      }))
      .sort((a: Prediction, b: Prediction) => b.probability - a.probability);

    const topPrediction = allPredictions[0];

    const lap = laplaceResult.squeeze().div(tf.scalar(255));

    const laplaceVariance = tf.moments(lap).variance.dataSync()[0] as number;
    const laplaceMax = lap.max().dataSync()[0] as number;
    const confidenceGap =
      allPredictions.length > 1
        ? allPredictions[0].probability - allPredictions[1].probability
        : 0;
    const entropy = -allPredictions.reduce(
      (sum, p) => sum + p.probability * Math.log(p.probability + 1e-12),
      0
    );

    const results: DetectionResults = {
      modelClass: topPrediction.className,
      modelScore: topPrediction.probability,
      modelProbability: topPrediction.probability,
      allPredictions,
      laplaceVariance,
      laplaceMax,
      confidenceGap,
      entropy,
    };

    //cleanup memory
    laplaceResult.dispose();
    modelResult.dispose();
    return results;
  }

  public async predict_image(
    canvas: HTMLCanvasElement,
    flipHorizontal: boolean,
    flipVertical: boolean
  ): Promise<DetectionResults | null> {
    //on slow systems the model might not be loaded yet.
    if (this.model === undefined) {
      return null;
    }

    //TODO check canvas matches model image size

    //cleans up all tensors on exit
    return tf.tidy(() => {
      let t0 = performance.now();
      let data = tf.browser.fromPixels(canvas, 3);

      if (flipHorizontal || flipVertical) {
        const flipAxes = [];
        if (flipHorizontal) flipAxes.push(1);
        if (flipVertical) flipAxes.push(0);
        data = data.reverse(flipAxes);
      }

      let grey = data.mean(2);
      data = tf.cast(data, "float32");
      //normalize from 0 to 1
      data = data.div(tf.scalar(255));

      GlobalLogger.debug(
        `image-prepare: ${performance.now() - t0} ms`,
        "detector"
      );

      const imgTensor4d = data.expandDims(0);

      t0 = performance.now();
      const result = this.model.predict(imgTensor4d);
      GlobalLogger.debug(
        `model-predict: ${performance.now() - t0} ms`,
        "detector"
      );

      grey = grey.expandDims(0).expandDims(-1);
      const lapT0 = performance.now();
      const lapResult = grey.conv2d(this.laplaceFilter, 1, "valid");
      GlobalLogger.debug(
        `laplacian: ${performance.now() - lapT0} ms`,
        "detector"
      );

      // Log memory usage periodically (2% of frames)
      if (Math.random() < 0.02) {
        GlobalLogger.debug(
          `Memory after embedding: ${this.tf_mem_log(tf.memory())}`,
          "detector"
        );
      }

      GlobalLogger.debug(this.tf_mem_log(tf.memory()), "detector");

      // Process results first, then handle embedding logic
      const finalResults = this.prepare_results(result, lapResult);

      // Add blur detection for all images
      const blurResults = this.detectBlur(imgTensor4d);
      if (blurResults) {
        finalResults.isSharp = blurResults.isSharp;
        finalResults.blurScore = blurResults.blurScore;
      }

      const distanceProblems = this.detectDistanceProblems(
        finalResults.allPredictions,
        finalResults.laplaceVariance,
        finalResults.blurScore ?? 0
      );
      if (distanceProblems.shouldReject) {
        const rejectionReason = distanceProblems.likelyTooFar
          ? "likely_too_far"
          : distanceProblems.likelyBlurry
          ? "likely_too_blurry"
          : "";
        return {
          ...finalResults,
          rejected: true,
          rejectionReason: rejectionReason,
        };
      }

      if (
        finalResults.modelClass === "card_front" ||
        finalResults.modelClass === "card_back"
      ) {
        this.processCardSimilarity(finalResults, imgTensor4d);
        finalResults.hasFlipped = finalResults.cardSimilarity! < 0.75;
        this.isFlippedSession ||= finalResults.hasFlipped;
        finalResults.isFlippedSession = this.isFlippedSession;
      }

      return finalResults;
    });
  }
}

class DetectorWrapper {
  public static image_size: number = CardDetector.IMG_SIZE;

  static get(): CardDetector {
    if (window.cardDetector === undefined) {
      window.cardDetector = new CardDetector();
    }
    return window.cardDetector;
  }

  static init(modelPath?: string) {
    if (window.cardDetector === undefined) {
      window.cardDetector = new CardDetector(modelPath);
    }
  }

  static cleanUp() {
    if (window.cardDetector) {
      window.cardDetector.disposeAll();
    }
    delete window.cardDetector;
  }
}

export class CardScanModel {
  static warm(modelPath?: string) {
    GlobalLogger.earlyLog("Detector -- warm model");
    DetectorWrapper.init(modelPath);
  }
}

export default DetectorWrapper;
